/**
 * DR6 — Deal Closing Pack Automation
 * Creates a single archived manifest (term sheet, executed docs, filings, cap-table snapshot)
 * when a deal moves to stage CLOSING or when "Generate Pack" is clicked.
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { Loader2, PackageCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export function DealClosingPack({ companyId }: { companyId: string }) {
  const [deals, setDeals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from('deal_rooms')
      .select('id,name,stage,closing_pack_url,updated_at')
      .eq('company_id', companyId)
      .order('updated_at', { ascending: false });
    if (error) toast.error(error.message);
    setDeals(data || []);
    setLoading(false);
  }

  async function generate(id: string, name: string) {
    setGenerating(id);
    toast.info('Building closing pack...');

    try {
      const { data, error } = await supabase.functions.invoke('generate-closing-pack', {
        body: { companyId, dealId: id },
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        toast.success(`Closing pack generated for ${name}`);
        load();
      } else {
        throw new Error(data?.error || 'Generation failed');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate closing pack');
    } finally {
      setGenerating(null);
    }
  }

  useEffect(() => {
    if (companyId) {
      load();
    }
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <PackageCheck className="h-5 w-5" /> Deal Closing Packs
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : deals.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Deal</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead>Pack</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deals.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="font-medium">{d.name}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-primary/10 text-primary">
                      {d.stage}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(d.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {d.closing_pack_url ? (
                      <a
                        href={d.closing_pack_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline hover:no-underline text-sm"
                      >
                        Download
                      </a>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      disabled={generating !== null}
                      onClick={() => generate(d.id, d.name)}
                    >
                      {generating === d.id ? (
                        <Loader2 className="animate-spin h-4 w-4" />
                      ) : (
                        <>
                          <PackageCheck className="h-4 w-4 mr-1" />
                          Generate Pack
                        </>
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-muted-foreground text-sm">No deals yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
